import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Sidebar } from './components/Sidebar';
import { CommandPalette } from './components/CommandPalette';
import { ToastProvider } from './components/primitives/Toast';
import { Week } from './pages/Week';
import { SectionPage } from './pages/SectionPage';
import { NoteDrawer } from './components/NoteDrawer';
import { NotesPage } from './pages/NotesPage';
import { NotesListPanel } from './components/notes/NotesListPanel';
import { SettingsPage } from './pages/SettingsPage';
import { LoginPage } from './pages/LoginPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { MobileShell } from './components/mobile/MobileShell';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useTheme } from './hooks/useTheme';
import { usePreferencesSync } from './hooks/usePreferencesSync';
import { useIsMobile } from './hooks/useIsMobile';
import { useDailyRollover } from './hooks/useDailyRollover';
import { useNotificationNavigation } from './hooks/useNotificationNavigation';
import { useUIStore } from './store/ui';
import './styles/theme.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 2,
      // Don't refetch on window focus during development to reduce noise
      refetchOnWindowFocus: import.meta.env.PROD,
    },
  },
});

function AppShell() {
  useTheme();
  useKeyboardShortcuts();
  usePreferencesSync();
  useDailyRollover();
  useNotificationNavigation();
  const isMobile = useIsMobile();
  // The notes list belongs to the notes view; it stays out of Week and Sections.
  const showNotesPanel = useUIStore((s) => s.notesPanelOpen) && useLocation().pathname.startsWith('/notes');

  if (isMobile) return <MobileShell />;

  return (
    <div
      style={{
        height: '100vh',
        padding: '24px',
        display: 'flex',
        justifyContent: 'center',
        position: 'relative',
        zIndex: 1,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          width: '100%',
          maxWidth: 'min(1800px, 100%)',
          background: 'var(--color-paper)',
          border: '1px solid var(--color-border)',
          borderRadius: '4px',
          boxShadow: 'var(--shadow-card)',
          overflow: 'hidden',
          height: '100%',
        }}
      >
        <Sidebar />
        {showNotesPanel && <NotesListPanel />}
        <main style={{ flex: 1, overflow: 'hidden', minWidth: 0, position: 'relative' }}>
          <NoteDrawer />
          <Routes>
            <Route path='/' element={<Navigate to='/week' replace />} />
            <Route path='/week' element={<Week />} />
            <Route path='/sections/:id' element={<SectionPage />} />
            <Route path='/notes' element={<NotesPage />} />
            <Route path='/notes/:id' element={<NotesPage />} />
            <Route path='/settings' element={<SettingsPage />} />
          </Routes>
        </main>
      </div>
      <CommandPalette />
    </div>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastProvider>
          <Routes>
            {/* Public route — no sidebar, no shell */}
            <Route path='/login' element={<LoginPage />} />

            {/* All other routes are protected by auth */}
            <Route element={<ProtectedRoute />}>
              <Route path='/*' element={<AppShell />} />
            </Route>
          </Routes>
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
